// Apolo IDE — Dual Mode side-panel (Sprint v11 E8 P6 second pass).
//
// Self-contained mini-composer + Architect/Implementer dual rendering. Lives
// in the SidePanel so it doesn't interfere with the regular streaming chat.
// Sends to /api/code-ide/dual-model, renders 2 side-by-side cards.

import { useCallback, useState } from 'react';
import { ArrowUp, BrainCircuit, Loader2, Wrench } from 'lucide-react';
import { useDualStream } from '../../../hooks/useDualStream.js';
import { extractEditsBlocks } from '../../../lib/multiFileEdits.js';

export default function DualModeTab({ sessionId }) {
  const [text, setText] = useState('');
  const dual = useDualStream();

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || dual.streaming) return;
    await dual.send({ userMessage: text.trim(), sessionId });
  }, [text, dual, sessionId]);

  const handleApplyEdits = useCallback(() => {
    if (!dual.execution) return;
    const edits = extractEditsBlocks(dual.execution);
    if (edits.length > 0) {
      window.dispatchEvent(new CustomEvent('apolo:multiEdits', {
        detail: { edits, source: 'dual-mode' },
      }));
    }
  }, [dual.execution]);

  if (!sessionId) {
    return (
      <div className="flex h-full items-center justify-center px-4 py-6 text-center text-xs text-apolo-textTertiary">
        Abra uma sessão para usar Architect+Implementer.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <form onSubmit={handleSubmit} className="border-b border-apolo-borderSubtle p-3">
        <label htmlFor="apolo-dual-input" className="mb-1 block text-[11px] font-medium text-apolo-textTertiary">
          Pergunta · Architect (Sonnet) planeja, Implementer (DeepSeek Flash) executa
        </label>
        <div className="flex gap-2">
          <textarea
            id="apolo-dual-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="ex: refatora useStreamingChat pra usar Effect ao invés de Promise"
            rows={3}
            disabled={dual.streaming}
            className="flex-1 resize-y rounded-md border border-apolo-borderSubtle bg-apolo-bg p-2 text-xs text-apolo-textPrimary outline-none focus:border-apolo-accent disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!text.trim() || dual.streaming}
            aria-label="Executar Architect+Implementer"
            className="flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-md bg-apolo-accent text-white transition-colors hover:bg-apolo-accentHover disabled:opacity-40"
          >
            {dual.streaming ? (
              <Loader2 size={13} className="animate-spin" aria-hidden="true" />
            ) : (
              <ArrowUp size={13} aria-hidden="true" />
            )}
          </button>
        </div>
        {dual.error && (
          <p role="alert" className="mt-2 text-[11px] text-apolo-error">
            {dual.error}
          </p>
        )}
      </form>

      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {(dual.plan || dual.phase === 'architect') && (
          <article className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-3">
            <header className="mb-2 flex items-center justify-between text-[11px]">
              <span className="inline-flex items-center gap-1 font-medium text-apolo-accent">
                <BrainCircuit size={11} aria-hidden="true" />
                Architect · Plan
              </span>
              {dual.phase === 'architect' && (
                <Loader2 size={10} className="animate-spin text-apolo-textTertiary" aria-hidden="true" />
              )}
            </header>
            <pre className="max-h-60 overflow-auto whitespace-pre-wrap text-[11px] text-apolo-textPrimary">
              {dual.plan || '—'}
            </pre>
          </article>
        )}

        {(dual.execution || dual.phase === 'implementer' || dual.phase === 'done') && (
          <article className="rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-3">
            <header className="mb-2 flex items-center justify-between text-[11px]">
              <span className="inline-flex items-center gap-1 font-medium text-apolo-success">
                <Wrench size={11} aria-hidden="true" />
                Implementer · Execute
              </span>
              {dual.phase === 'implementer' && (
                <Loader2 size={10} className="animate-spin text-apolo-textTertiary" aria-hidden="true" />
              )}
              {dual.phase === 'done' && dual.execution.includes('<edits>') && (
                <button
                  type="button"
                  onClick={handleApplyEdits}
                  className="rounded-md border border-apolo-accent/40 bg-apolo-accentMuted px-2 py-0.5 text-[10px] font-medium text-apolo-accent hover:bg-apolo-accent/20"
                >
                  Send to Edits tab
                </button>
              )}
            </header>
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap text-[11px] text-apolo-textPrimary">
              {dual.execution || '—'}
            </pre>
          </article>
        )}

        {dual.phase === 'idle' && !dual.plan && !dual.execution && (
          <p className="text-center text-xs text-apolo-textTertiary">
            Envie uma pergunta acima. Architect (Sonnet) planeja em ## Plan / ## Files / ## Risk,
            depois Implementer (DeepSeek Flash) executa em &lt;edits&gt; blocks.
          </p>
        )}
      </div>
    </div>
  );
}
